import React, { useState } from 'react';
import { MapPin, Clock, Star, Bookmark, Car, Footprints, Bus, Train, Mountain, Utensils, Coffee, Camera, Building2, Trees, Waves, ShoppingBag, Music, Theater, Plane, Ship, Home, Palette, Book, Gamepad2, Dumbbell, Heart } from 'lucide-react';
import { DayPlan, Activity } from '../../types/itinerary';
import { findPlace } from '../../services/places';
import { ActivityShimmer } from '../ui/Shimmer';
import { AnimatePresence, motion } from 'framer-motion';
import { TransportIcon } from './TransportIcon';

// Import color sequence at the top
import { COLOR_SEQUENCE, MARKER_COLORS } from '../../utils/markerUtils';

interface DayTimelineProps {
  day: DayPlan;
  onLocationSelect: (locationId: string) => void;
  selectedLocationId?: string;
  isLoading?: boolean;
  previousDayComplete?: boolean;
  dayNumber: number;
  streamingActivity?: boolean;
  activeDay?: number;
  dayColor?: string;
}

// Function to get relevant icon based on activity name/type
function getActivityIcon(activity: Activity) {
  const name = activity.name.toLowerCase();
  const category = activity.category?.toLowerCase() || '';
  
  // Try to use emoji first, with Lucide icon as fallback
  
  // Food & Dining
  if (name.includes('breakfast') || name.includes('brunch')) return '🥞';
  if (name.includes('lunch')) return '🍽️';
  if (name.includes('dinner')) return '🍷';
  if (name.includes('restaurant') || name.includes('eat') || name.includes('food') || category === 'food') return '🍴';
  if (name.includes('coffee') || name.includes('cafe') || name.includes('café')) return '☕';
  if (name.includes('bar') || name.includes('pub')) return '🍺';
  if (name.includes('ice cream') || name.includes('dessert')) return '🍦';
  
  // Transportation
  if (name.includes('airport') || name.includes('flight')) return '✈️';
  if (name.includes('tram')) return '🚊';
  if (name.includes('train') || name.includes('railway')) return '🚂';
  if (name.includes('metro') || name.includes('subway')) return '🚇';
  if (name.includes('bus')) return '🚌';
  if (name.includes('boat') || name.includes('cruise') || name.includes('ferry')) return '⛴️';
  if (name.includes('gondola') || name.includes('cable car')) return '🚡';
  
  // Nature & Outdoors
  if (name.includes('beach') || name.includes('bay')) return '🏖️';
  if (name.includes('ocean') || name.includes('sea')) return '🌊';
  if (name.includes('park') || name.includes('garden') || name.includes('botanical')) return '🌳';
  if (name.includes('mountain') || name.includes('peak') || name.includes('hill')) return '⛰️';
  if (name.includes('hike') || name.includes('trail')) return '🥾';
  if (name.includes('lake')) return '🏞️';
  if (name.includes('waterfall')) return '💦';
  
  // Culture & Entertainment
  if (name.includes('museum')) return '🏛️';
  if (name.includes('art') || name.includes('gallery')) return '🎨';
  if (name.includes('theater') || name.includes('theatre')) return '🎭';
  if (name.includes('music') || name.includes('concert')) return '🎵';
  if (name.includes('opera')) return '🎼';
  if (name.includes('movie') || name.includes('cinema')) return '🎬';
  if (name.includes('show') || name.includes('performance')) return '🎪';
  
  // Activities & Shopping
  if (name.includes('shopping') || name.includes('mall')) return '🛍️';
  if (name.includes('market')) return '🛒';
  if (name.includes('spa') || name.includes('wellness')) return '💆';
  if (name.includes('gym') || name.includes('fitness')) return '🏋️';
  if (name.includes('sport')) return '⚽';
  if (name.includes('swim') || name.includes('pool')) return '🏊';
  if (name.includes('golf')) return '⛳';
  if (name.includes('ski')) return '⛷️';
  
  // Buildings & Landmarks
  if (name.includes('tower')) return '🗼';
  if (name.includes('castle')) return '🏰';
  if (name.includes('church') || name.includes('cathedral')) return '⛪';
  if (name.includes('temple')) return '🛕';
  if (name.includes('mosque')) return '🕌';
  if (name.includes('bridge')) return '🌉';
  if (name.includes('hotel') || name.includes('accommodation')) return '🏨';
  if (name.includes('hospital') || name.includes('medical')) return '🏥';
  if (name.includes('school') || name.includes('university')) return '🏫';
  if (name.includes('library')) return '📚';
  if (name.includes('stadium')) return '🏟️';
  
  // Tours & Sightseeing
  if (name.includes('tour') || name.includes('sightseeing') || category === 'sightseeing') return '🚶';
  if (name.includes('photo') || name.includes('photography')) return '📸';
  if (name.includes('view') || name.includes('lookout') || name.includes('observation')) return '👀';
  
  // Nightlife
  if (name.includes('club') || name.includes('nightclub')) return '🕺';
  if (name.includes('casino')) return '🎰';
  
  // Special Activities
  if (name.includes('zoo')) return '🦁';
  if (name.includes('aquarium')) return '🐠';
  if (name.includes('amusement') || name.includes('theme park')) return '🎢';
  if (name.includes('festival')) return '🎉';
  if (name.includes('wine') || name.includes('vineyard')) return '🍷';
  
  // Default fallback to Lucide icon
  return <MapPin className="w-6 h-6" />;
}

export function DayTimeline({ 
  day, 
  onLocationSelect,
  selectedLocationId,
  isLoading = false,
  previousDayComplete = true,
  dayNumber,
  streamingActivity = false,
  activeDay,
  dayColor = '#007AFF'  
}: DayTimelineProps) {
  const [activeActivity, setActiveActivity] = useState<string | null>(null);
  
  const colorKey = COLOR_SEQUENCE[(dayNumber - 1) % COLOR_SEQUENCE.length];
  const timelineColor = MARKER_COLORS[colorKey];
  
  const shouldShowShimmer = React.useMemo(() => {
    if (day.activities.length === 0) return true;
    if (streamingActivity && activeDay === dayNumber) return true;
    return false;
  }, [day.activities.length, streamingActivity, dayNumber, activeDay]);

  return (
    <div className="relative" style={{ 
      paddingTop: 'var(--timeline-day-padding-top, 1.5rem)',
      paddingBottom: 'var(--timeline-day-padding-bottom, 1.2rem)',
      backgroundColor: 'transparent'
    }}>
      {/* Timeline line for this day */}
      <div 
        className="absolute top-0 bottom-0"
        style={{ 
          left: 'var(--timeline-line-left, 46px)',
          width: 'var(--timeline-line-thickness, 3px)',
          backgroundColor: timelineColor,
          opacity: 'var(--timeline-line-opacity, 0.3)'
        }}
      />
      
      {/* Activities */}
      <div className="space-y-0">
        <AnimatePresence mode="sync">
          {day.activities.map((activity, index) => (
            <ActivityCard
              key={activity.id}
              activity={activity}
              isSelected={selectedLocationId === activity.location.id}
              isActive={activeActivity === activity.id}
              onSelect={() => {
                onLocationSelect(activity.location.id);
                window.dispatchEvent(new CustomEvent('tripper-location-select', {
                  detail: { location: activity.location }
                }));
              }}
              onToggleActive={() => setActiveActivity(activeActivity === activity.id ? null : activity.id)}
              onMapsClick={(e) => handleMapsClick(e, activity.location)}
              color={timelineColor}
              isFirst={index === 0}
              isLast={index === day.activities.length - 1}
              nextActivity={index < day.activities.length - 1 ? day.activities[index + 1] : null}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

interface ActivityCardProps {
  activity: Activity;
  isSelected: boolean;
  isActive: boolean;
  onSelect: () => void;
  onToggleActive: () => void;
  onMapsClick: (e: React.MouseEvent) => void;
  color: string;
  isFirst: boolean;
  isLast: boolean;
  nextActivity: Activity | null;
}

function ActivityCard({ 
  activity, 
  isSelected, 
  isActive,
  onSelect, 
  onToggleActive,
  onMapsClick,
  color,
  isFirst,
  isLast,
  nextActivity
}: ActivityCardProps) {
  const getTransportIcon = (transport: string) => {
    switch(transport) {
      case 'Walk': return <Footprints className="w-4 h-4" />;
      case 'Taxi': 
      case 'Car': return <Car className="w-4 h-4" />;
      case 'Bus': return <Bus className="w-4 h-4" />;
      case 'Train': return <Train className="w-4 h-4" />;
      default: return <Car className="w-4 h-4" />;
    }
  };

  const activityIcon = getActivityIcon(activity);

  // Auto-open card when location is selected (from map click)
  // Auto-close card when location is deselected
  React.useEffect(() => {
    if (isSelected && !isActive) {
      onToggleActive();
    } else if (!isSelected && isActive) {
      onToggleActive();
    }
  }, [isSelected]);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="relative flex items-start"
      data-activity-id={activity.location.id}
      onMouseEnter={() => {
        // Dispatch event for map hover - using the correct event name
        window.dispatchEvent(new CustomEvent('tripper-location-hover', {
          detail: { locationId: activity.location.id }
        }));
      }}
      onMouseLeave={() => {
        window.dispatchEvent(new CustomEvent('tripper-location-hover-end'));
      }}
    >
      {/* Time */}
      <div 
        className="absolute text-right"
        style={{ 
          left: `var(--timeline-time-x-offset, 0px)`,
          top: `var(--timeline-time-y-offset, 2px)`,
          width: "var(--timeline-time-width, 40px)",
          fontSize: "var(--timeline-time-font-size, 0.875rem)",
          fontWeight: "var(--timeline-time-font-weight, 500)",
          color: "var(--timeline-time-color, #4b5563)"
        }}
      >
        {activity.startTime}
      </div>
      
      {/* Dot with white outline */}
      <div 
        className="absolute flex items-center justify-center"
        style={{ 
          left: "var(--timeline-dot-left, 40px)",
          top: "2px",
          transform: "translate(-50%, 0)" // Center horizontally
        }}
      >
        <div 
          className={`
            rounded-full transition-all duration-200
            ${isActive 
              ? 'bg-white border-[3px]' 
              : 'bg-current border-2 border-white'
            }
          `}
          style={{ 
            width: isActive ? "var(--timeline-active-dot-size, 1rem)" : "var(--timeline-inactive-dot-size, 0.75rem)",
            height: isActive ? "var(--timeline-active-dot-size, 1rem)" : "var(--timeline-inactive-dot-size, 0.75rem)",
            borderColor: isActive ? color : undefined,
            backgroundColor: isActive ? 'white' : color,
            boxShadow: "var(--timeline-dot-shadow, 0 0 0 2px rgba(20, 184, 166, 0.2))"
          }}
        />
      </div>
      
      {/* Card */}
      <div className="flex-1 pb-6 pl-8 pr-6">
        <div 
          onClick={() => {
            onToggleActive();
            onSelect(); // Trigger map movement
          }}
          className={`bg-white rounded-lg cursor-pointer transition-all duration-200`}
          style={{ 
            border: 'none',
            width: 'var(--activity-card-width, 100%)',
            minHeight: isActive ? 'var(--activity-card-open-max-height, auto)' : 'var(--activity-card-closed-height, auto)',
            padding: 'var(--activity-card-padding, 1rem)',
            transform: `translate(var(--activity-card-x-offset, 0px), var(--activity-card-y-offset, 0px)) scale(${
              isActive ? '1' : '1'
            })`,
            backgroundColor: isActive ? 'var(--activity-card-active-bg, #fafaf00)' : 'white',
            boxShadow: isActive 
              ? 'var(--activity-card-active-shadow, 0 4px 12px rgba(0,0,0,0.1))' 
              : 'none',
            outline: isActive ? `var(--activity-card-selected-ring-width, 2px) solid ${color}` : 'none',
            outlineOffset: '2px'
          }}
          onMouseEnter={(e) => {
            if (!isActive) {
              e.currentTarget.style.boxShadow = 'var(--activity-card-hover-shadow, 0 10px 25px rgba(0,0,0,0.08))';
              e.currentTarget.style.transform = `translate(var(--activity-card-x-offset, 0px), var(--activity-card-y-offset, 0px)) scale(var(--activity-card-hover-scale, 1.01))`;
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive) {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = `translate(var(--activity-card-x-offset, 0px), var(--activity-card-y-offset, 0px)) scale(1)`;
            }
          }}
        >
          <div className="flex items-start gap-3">
            {/* Icon with colored background */}
            <div 
              className="rounded-full flex items-center justify-center flex-shrink-0 relative"
              style={{
                width: "var(--activity-card-icon-bg-size, 2.5rem)",
                height: "var(--activity-card-icon-bg-size, 2.5rem)"
              }}
            >
              <div 
                className="absolute inset-0 rounded-full"
                style={{
                  backgroundColor: color,
                  opacity: "var(--activity-card-icon-bg-opacity, 0.15)"
                }}
              />
              <span style={{ 
                fontSize: "var(--activity-card-icon-size, 1.75rem)",
                position: 'relative',
                zIndex: 1
              }}>
                {activityIcon}
              </span>
            </div>
            <div className="flex-1">
              <h4 
                className="font-medium text-gray-900"
                style={{ 
                  fontSize: "var(--activity-card-title-size, 1rem)",
                  fontFamily: "var(--activity-card-title-font-family, inherit)"
                }}
              >
                {activity.name}
              </h4>
              <div 
                className="flex items-center gap-4 mt-1 text-gray-500"
                style={{ fontSize: "var(--activity-card-meta-size, 0.875rem)" }}
              >
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {activity.duration}
                </span>
                <span className="font-medium text-gray-700">
                  {activity.cost === '$0' ? 'Free' : activity.cost}
                </span>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Add bookmark functionality here if needed
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Bookmark 
                style={{ 
                  width: "var(--activity-card-bookmark-size, 1.25rem)",
                  height: "var(--activity-card-bookmark-size, 1.25rem)"
                }}
              />
            </button>
          </div>
          
          <AnimatePresence mode="wait">
            {isActive && (
              <motion.div
                key="expanded-content"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ 
                  height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                  opacity: { duration: 0.2, ease: "easeOut" }
                }}
                style={{ willChange: 'height' }}
                className="overflow-hidden"
              >
                <div className="mt-2 pt-4 border-none space-y-7">
                  <p 
                    className="text-gray-600"
                    style={{ fontSize: "var(--activity-card-description-size, 0.875rem)" }}
                  >
                    {activity.description}
                  </p>
                  <div className="flex items-center justify-between text-gray-500" style={{ fontSize: "var(--activity-card-meta-size, 0.875rem)" }}>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{activity.location.name}</span>
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onMapsClick(e);
                      }}
                      className="transition-colors"
                      style={{
                        color: 'var(--activity-card-link-color, #2563eb)',
                        fontSize: 'var(--activity-card-link-size, 0.875rem)',
                        fontWeight: 'var(--activity-card-link-weight, 500)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--activity-card-link-hover-color, #1d4ed8)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--activity-card-link-color, #2563eb)';
                      }}
                    >
                      View on map
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Transport to next */}
        {/* {nextActivity && (
          <div 
            className="flex items-center gap-1 mt-2 text-gray-500 ml-4"
            style={{ 
              fontSize: "var(--timeline-transport-font-size, 0.875rem)",
              gap: "var(--timeline-transport-gap, 0.5rem)",
              transform: `translate(var(--timeline-transport-x-offset, 0px), var(--timeline-transport-y-offset, 0px))`
            }}
          >
            {getTransportIcon(nextActivity.transport || 'Car')}
            <span>{nextActivity.travelTime} to next</span>
          </div>
        )} */}
      </div>
    </motion.div>
  );
}

function handleMapsClick(e: React.MouseEvent, location: any) {
  e.stopPropagation();
  try {
    findPlace(location).then(mapsUrl => {
      window.open(mapsUrl, '_blank');
    });
  } catch (error) {
    console.error('Error opening maps:', error);
  }
}